import {
  Input,
  Output,
  OnChanges,
  Component,
  EventEmitter,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { endOfMinute, startOfMinute } from 'date-fns';

import { SuiDateRange } from '@sensika/sui/date-picker';

import {
  isQuickPeriodValue,
  quickPeriodRange,
} from './date-range-selection.presets';

// * Presentational range panel: preset bar + inline range calendar + time
// * pickers. Store-free by design (WAP-1590) - context-specific concerns
// * (period history, company date bounds) are supplied by the consumer via
// * the `range-panel-extras` content slot and the minDate/maxDate inputs.
@Component({
  selector: 'app-date-range-selection',
  templateUrl: './date-range-selection.component.html',
  styleUrls: ['./date-range-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DateRangeSelectionComponent implements OnChanges {
  @Input() fromDate: Date;
  @Input() toDate: Date;
  @Input() presetBar: boolean = true;
  /** Optional second preset row with now-relative minute/hour durations (grid filters opt in). */
  @Input() timePresetBar: boolean = false;
  @Input() timeDetails: boolean = true;
  /** Compact calendars (26px day squares) for views that must fit a 1080px viewport. */
  @Input() compact: boolean = false;
  /** Calendar bounds; consumers typically bind selectCompanyDateBounds. */
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Output() dateRangeSelection = new EventEmitter<{ from: Date; to: Date }>();

  /** Day-level value fed to the inline range calendar (full datetime lives on fromDate/toDate). */
  rangeValue: SuiDateRange = { start: null, end: null };
  /** Pin the calendar to [previous month, current month] (current on the right). */
  readonly initialMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth() - 1,
    1,
  );
  // Active quick-period option value ('1Y' … '24H' | 'today', or a time preset
  // '5m' … '12h'), or null when the current range is custom (no preset
  // highlighted). Both preset rows share it - a value only ever exists in one
  // row, so selecting in either row clears the highlight in the other.
  // Matched by value so it works regardless of the (translated) label.
  activePeriod: string | null = null;

  dateRangePeriods = [
    { label: '1Y', value: '1Y' },
    { label: '6M', value: '6M' },
    { label: '3M', value: '3M' },
    { label: '1M', value: '1M' },
    { label: '2W', value: '2W' },
    { label: '1W', value: '1W' },
    { label: '3D', value: '3D' },
    { label: '24H', value: '24H' },
    {
      label: this.translateService.instant('date-range-selection.label.today'),
      value: 'today',
    },
  ];

  // Now-relative durations for the optional second row. Lowercase on purpose:
  // uppercase in the row above means calendar periods ('1M' = month), lowercase
  // here means clock durations from now ('5m' = minutes) - Grafana convention.
  timePresetPeriods = [
    { label: '12h', value: '12h' },
    { label: '8h', value: '8h' },
    { label: '4h', value: '4h' },
    { label: '1h', value: '1h' },
    { label: '30m', value: '30m' },
    { label: '15m', value: '15m' },
    { label: '5m', value: '5m' },
  ];

  constructor(private translateService: TranslateService) {}

  /** The range last emitted, so an echo of it from the consumer is recognised. */
  private lastEmitted: { from: number | null; to: number | null } | null = null;

  ngOnChanges(changes: SimpleChanges) {
    // Reflect the bound range into the calendar highlight. A range that
    // differs from the panel's own last emission came from outside (e.g. a
    // history period) - drop the preset highlight and re-sync the calendar.
    // An echo of the panel's own emission (round-tripped by the consumer)
    // must leave the calendar alone: after the first click it holds a start
    // with the end still pending, and syncing the emitted one-day range into
    // it would turn the next click into a fresh range instead of the end.
    if (changes['fromDate'] || changes['toDate']) {
      if (this.isEchoOfOwnEmission()) {
        return;
      }
      this.activePeriod = null;
      this.syncRangeValue();
    }
  }

  private isEchoOfOwnEmission(): boolean {
    return (
      !!this.lastEmitted &&
      this.lastEmitted.from === (this.fromDate?.getTime() ?? null) &&
      this.lastEmitted.to === (this.toDate?.getTime() ?? null)
    );
  }

  private emit() {
    this.lastEmitted = {
      from: this.fromDate?.getTime() ?? null,
      to: this.toDate?.getTime() ?? null,
    };
    this.dateRangeSelection.emit({ from: this.fromDate, to: this.toDate });
  }

  // * Inline range calendar selection. Picking days resets the time to
  // * 00:00 / 23:59:59 (the time pickers below refine it). The first click is
  // * already a complete one-day range and is emitted as such, so what the
  // * inputs show is what Apply applies (WAP-1463); the calendar keeps waiting
  // * for the second click (see ngOnChanges), which widens the range - the
  // * primitive orders an earlier second day start-first, the swap below only
  // * guards against a reversed pair reaching this panel some other way.
  onRangeChange(range: SuiDateRange): void {
    this.activePeriod = null;
    let start = range?.start ?? null;
    let end = range?.end ?? null;
    if (start && end && end < start) {
      [start, end] = [end, start];
    }
    this.rangeValue = { start, end };
    if (!start) {
      return;
    }

    this.fromDate = start;
    this.toDate = this.endOfDay(end ?? start);
    this.normalizeRange();
    this.emit();
  }

  // * Seconds are never part of a range: the range travels into query cache
  // * keys (the memcached layer in front of Solr), and 'now'-seeded values
  // * (24H preset, time-picker seeds) would make every selection a unique key.
  // * Normalized to the established inclusive convention: start :00, end :59.
  private normalizeRange(): void {
    if (this.fromDate) {
      this.fromDate = startOfMinute(this.fromDate);
    }
    if (this.toDate) {
      this.toDate = endOfMinute(this.toDate);
    }
  }

  /** Helper: push a Date to 23:59:59.0 */
  private endOfDay(d: Date): Date {
    const e = new Date(d);
    e.setHours(23, 59, 59, 0);
    return e;
  }

  /** Mirror fromDate/toDate into the inline range calendar (day part only). */
  private syncRangeValue(): void {
    this.rangeValue = {
      start: this.fromDate ?? null,
      end: this.toDate ?? null,
    };
  }

  setQuickPeriod(period: string) {
    // Unknown period
    if (!isQuickPeriodValue(period)) return;

    // Range computation lives in date-range-selection.presets.ts so consumers
    // can resolve a named preset without the component; already normalized.
    const { from, to } = quickPeriodRange(period);
    this.fromDate = from;
    this.toDate = to;

    this.activePeriod = period;
    this.syncRangeValue();
    this.emit();
  }

  // * Time presets are now-relative ("last N minutes/hours"), unlike the
  // * calendar presets above which anchor to today. normalizeRange() then
  // * clamps the live seconds to the :00/:59 convention - the emitted range
  // * must never carry them (memcached keys, see normalizeRange).
  setQuickTimePeriod(period: string) {
    const durationMinutes: Record<string, number> = {
      '5m': 5,
      '15m': 15,
      '30m': 30,
      '1h': 60,
      '4h': 240,
      '8h': 480,
      '12h': 720,
    };

    const minutes = durationMinutes[period];
    if (!minutes) return;

    this.toDate = new Date();
    this.fromDate = new Date(this.toDate.getTime() - minutes * 60 * 1000);

    this.normalizeRange();
    this.activePeriod = period;
    this.syncRangeValue();
    this.emit();
  }

  onFromDateChange(newDate: Date) {
    // A field edit may change the day → keep the calendar highlight in sync.
    this.normalizeRange();
    this.syncRangeValue();
    this.emit();
  }
}
